import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ClickableLabel from 'react/components/common/ClickableLabel/ClickableLabel';
import AudienceTarget from 'react/components/apps/audience/AudienceBuilder/AudienceTarget/AudienceTarget';
import {
  getSegmentIds,
  getAmplifiedEstimatedReachGoal,
  createAmplifiedAudience,
  createAlwaysOnAudience,
  refreshAlwaysOnAudience,
  createDeterministicAudience,
  createUserListForDeterministicAudience,
  getDataContract,
  getTaxonomyCategory,
  getAmpModelCategory,
} from 'react/services/AudienceInsightsService';

/**
 * Bottom bar component containing targets and audience activation
 */
const AudienceInsightsBottomBar = ({
  // Target props
  showTargets,
  targets,
  onOpenTargets,

  // Activation state props
  isActivateEnabled,
  activation,

  // Audience props
  audienceId,
  audienceName,
  segment,
  audienceChannel,

  // Services
  notificator,
  dspService,
  ssoService,

  // Handlers
  handleActivatingAudience,
  handleActivatingTarget,

  // Category selection
  selectedCategory,
  onActivateSelectedCategory,
  isCategoryTargetingVisible,
}) => {
  if (!showTargets && !isActivateEnabled) {
    return null;
  }

  return (
    <div className="audience-insights-bottom-bar">
      {showTargets && (
        <div
          className={classNames('inline-block right target-buttons', {
            'has-audience-target': isActivateEnabled,
          })}
        >
          <ClickableLabel
            label="Load Target"
            leftIconId="upload"
            onClick={onOpenTargets}
            isDisabled={!targets || targets.length === 0}
            onDisabledTooltip="There are no saved audiences in this program"
          />
        </div>
      )}
      {isActivateEnabled && (
        <div className="audience-target">
          <AudienceTarget
            marketsAndAdvertisersPromise={activation.marketsAndAdvertisersPromise}
            audienceId={audienceId}
            audienceName={audienceName}
            audienceSegment={segment}
            isActivateEnabled={isActivateEnabled}
            isActivateAudienceEnabled={activation.isActivateAudienceEnabledState}
            isActivateAudienceVisible={activation.isActivateAudienceVisible}
            activateAudienceDisabledText={activation.activateAudienceDisabledText}
            isActivateTargetEnabled={showTargets}
            isAudienceDeterministicActivated={activation.isAudienceDeterministicActivated}
            isAudienceAmplifiedActivated={activation.isAudienceAmplifiedActivated}
            isAudienceAlwaysOnActivated={activation.isAudienceAlwaysOnActivated}
            activatedAmplifiedThreshold={activation.activatedAmplifiedThreshold}
            activatedAlwaysOnThreshold={activation.activatedAlwaysOnThreshold}
            activatedMarket={activation.activatedMarket}
            activatedAdvertiser={activation.activatedAdvertiser}
            activatedDataContractId={activation.activatedDataContractId}
            activatedDataContractText={activation.activatedDataContractText}
            activatedCategoryId={activation.activatedCategoryId}
            channel={audienceChannel}
            getSegmentIds={getSegmentIds}
            createAmplifiedAudience={createAmplifiedAudience}
            createAlwaysOnAudience={createAlwaysOnAudience}
            refreshAlwaysOnAudience={refreshAlwaysOnAudience}
            createDeterministicAudience={createDeterministicAudience}
            createUserListForDeterministicAudience={createUserListForDeterministicAudience}
            notificator={notificator}
            updateAudienceDeterministicActivated={activation.updateAudienceDeterministicActivated}
            updateAudienceAmplifiedActivated={activation.updateAudienceAmplifiedActivated}
            updateAudienceAlwaysOnActivated={activation.updateAudienceAlwaysOnActivated}
            dspService={dspService}
            ssoService={ssoService}
            getAmplifiedEstimatedReachGoal={getAmplifiedEstimatedReachGoal}
            handleActivatingAudience={handleActivatingAudience}
            handleActivatingTarget={handleActivatingTarget}
            widgetName="interests"
            hasAmplificationModeSelector={activation.hasAmplificationModeSelector}
            getDataContract={getDataContract}
            getTaxonomyCategory={getTaxonomyCategory}
            getAmpModelCategory={getAmpModelCategory}
            isDeterministicDynamicAllowed={activation.isDeterministicDynamicAllowed}
            allowDeterministicSegment={activation.allowDeterministicSegment}
            allowCustomPriceSegment={activation.allowCustomPriceSegment}
            allowOptimizeSegment={activation.allowOptimizeSegment}
            selectedCategory={selectedCategory}
            onActivateSelectedCategory={onActivateSelectedCategory}
            isCategoryTargetingVisible={isCategoryTargetingVisible}
          />
        </div>
      )}
    </div>
  );
};

AudienceInsightsBottomBar.propTypes = {
  showTargets: PropTypes.bool,
  targets: PropTypes.array,
  onOpenTargets: PropTypes.func,
  isActivateEnabled: PropTypes.bool,
  activation: PropTypes.shape({
    marketsAndAdvertisersPromise: PropTypes.object,
    isActivateAudienceEnabledState: PropTypes.bool,
    isActivateAudienceVisible: PropTypes.bool,
    activateAudienceDisabledText: PropTypes.string,
    isAudienceDeterministicActivated: PropTypes.bool,
    isAudienceAmplifiedActivated: PropTypes.bool,
    isAudienceAlwaysOnActivated: PropTypes.bool,
    activatedAmplifiedThreshold: PropTypes.number,
    activatedAlwaysOnThreshold: PropTypes.number,
    activatedMarket: PropTypes.object,
    activatedAdvertiser: PropTypes.object,
    activatedDataContractId: PropTypes.string,
    activatedDataContractText: PropTypes.string,
    activatedCategoryId: PropTypes.string,
    hasAmplificationModeSelector: PropTypes.bool,
    isDeterministicDynamicAllowed: PropTypes.bool,
    allowDeterministicSegment: PropTypes.bool,
    allowCustomPriceSegment: PropTypes.bool,
    allowOptimizeSegment: PropTypes.bool,
    updateAudienceDeterministicActivated: PropTypes.func,
    updateAudienceAmplifiedActivated: PropTypes.func,
    updateAudienceAlwaysOnActivated: PropTypes.func,
  }),
  audienceId: PropTypes.string,
  audienceName: PropTypes.string,
  segment: PropTypes.array,
  audienceChannel: PropTypes.string,
  notificator: PropTypes.object,
  dspService: PropTypes.object,
  ssoService: PropTypes.object,
  handleActivatingAudience: PropTypes.func,
  handleActivatingTarget: PropTypes.func,
  selectedCategory: PropTypes.object,
  onActivateSelectedCategory: PropTypes.func,
  isCategoryTargetingVisible: PropTypes.bool,
};

export default AudienceInsightsBottomBar;
